import { z } from 'zod';
/**
 * Memory anchors (DEV-17252, memory-architecture §4.4) — the v1 vocabulary of
 * `AgentMemory.subjectRef` keys that name WHAT a memory is about: the device,
 * robot class, mission, and place a lesson applies to. `subjectRef` itself
 * stays a free-form within-set filter that never grants access; this module
 * only fixes the vocabulary the deterministic stampers and the reconcile
 * backfill agree on, so retrieval's anchor matching has something to match.
 *
 * Three sources write anchors, cheapest first (§4.4 / A.1.21):
 *   1. write-time auto-stamping — a `save_memory` issued during a turn
 *      inherits the turn's anchor set (the waking event's attributes plus the
 *      agent's static `config.anchors` scope), zero model calls;
 *   2. the tool schema's explicit `subjectRef`, for when the model knows
 *      better than the ambient context (a memory about the destination, say);
 *   3. the reconcile job's backfill for rows that lack them (§4.6).
 */
/**
 * The v1 anchor extractors (§4.4, decided): device/class/mission ride the
 * agent's scope and event attributes for free; `destinationPlaceId` comes from
 * the mission-carries-target convention. Turn-parsing for named places is
 * deliberately deferred until the eval shows it matters.
 */
export const memoryAnchorKeys = [
    'deviceId',
    'robotClass',
    'missionId',
    'siteId',
    'placeId',
    'destinationPlaceId',
];
/** Max length of one anchor value — anchors are ids/short names, never prose. */
export const memoryAnchorValueMaxLength = 200;
const anchorValue = z.string().min(1).max(memoryAnchorValueMaxLength);
/**
 * A typed anchor set — every key optional; an empty object is treated as "no
 * anchors" by the stampers. Strict so a typo'd key is a validation error at
 * the tool edge instead of a silently dead filter.
 */
export const MemoryAnchorSetSchema = z
    .object({
    deviceId: anchorValue.optional(),
    robotClass: anchorValue.optional(),
    missionId: anchorValue.optional(),
    siteId: anchorValue.optional(),
    placeId: anchorValue.optional(),
    destinationPlaceId: anchorValue.optional(),
})
    .strict();
/**
 * Deterministically pick the whitelisted anchor keys out of a loosely-typed
 * attribute bag (a `PersistentAgentEvent.attributes` payload, an agent's
 * `config.anchors`). Non-string scalars are stringified (device ids arrive as
 * numbers from some producers); empty/oversized values and non-scalar values
 * are dropped. Returns undefined when nothing usable remains, so callers can
 * spread-omit cleanly.
 */
export function extractMemoryAnchors(source) {
    if (!source || typeof source !== 'object')
        return undefined;
    const out = {};
    for (const key of memoryAnchorKeys) {
        const raw = source[key];
        if (raw === null || raw === undefined)
            continue;
        if (typeof raw !== 'string' && typeof raw !== 'number' && typeof raw !== 'boolean')
            continue;
        const value = String(raw).trim();
        if (value.length === 0 || value.length > memoryAnchorValueMaxLength)
            continue;
        out[key] = value;
    }
    return Object.keys(out).length > 0 ? out : undefined;
}
//# sourceMappingURL=memory-anchors.js.map